import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent 
} from './ui/card';
import { Button } from './ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from './ui/table';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  TrendingUp, 
  Users, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  ArrowRight,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROMOTION_STATUS = {
  ADMIS: { label: 'Admis', color: 'text-green-600 bg-green-50', icon: CheckCircle2 },
  REDOUBLANT: { label: 'Redoublant', color: 'text-orange-600 bg-orange-50', icon: AlertCircle },
  QUITTE: { label: 'A quitté', color: 'text-gray-600 bg-gray-50', icon: XCircle },
  TRANSFERE: { label: 'Transféré', color: 'text-blue-600 bg-blue-50', icon: ArrowRight },
  PASSAGE_ANTICIPE: { label: 'Passage anticipé', color: 'text-purple-600 bg-purple-50', icon: TrendingUp }
};

const CLASS_LEVELS = {
  // Maternelle
  PETITE_SECTION: 'Petite Section',
  MOYENNE_SECTION: 'Moyenne Section',
  GRANDE_SECTION: 'Grande Section',
  // Primaire
  CP1: 'CP1',
  CP2: 'CP2',
  CE1: 'CE1',
  CE2: 'CE2',
  CM1: 'CM1',
  CM2: 'CM2',
  // Collège
  SIXIEME: '6ème',
  CINQUIEME: '5ème',
  QUATRIEME: '4ème',
  TROISIEME: '3ème',
  // Lycée général
  SECONDE: 'Seconde',
  PREMIERE: 'Première',
  TERMINALE: 'Terminale',
  // Lycée technique
  ANNEE_7: '7ème année',
  ANNEE_8: '8ème année',
  ANNEE_9: '9ème année',
  ANNEE_10: '10ème année',
  ANNEE_11_SCIENCES_EXP: '11ème année Sciences Expérimentales',
  ANNEE_11_SCIENCES_MATH: '11ème année Sciences Mathématiques',
  ANNEE_11_SCIENCES_SOC: '11ème année Sciences Sociales',
  ANNEE_12_SCIENCES_EXP: '12ème année Sciences Expérimentales',
  ANNEE_12_SCIENCES_MATH: '12ème année Sciences Mathématiques',
  ANNEE_12_SCIENCES_SOC: '12ème année Sciences Sociales',
  ANNEE_13_SCIENCES_EXP: '13ème année Sciences Expérimentales',
  ANNEE_13_SCIENCES_MATH: '13ème année Sciences Mathématiques',
  ANNEE_13_SCIENCES_SOC: '13ème année Sciences Sociales'
};

const NEXT_LEVEL_MAPPING = {
  PETITE_SECTION: 'MOYENNE_SECTION',
  MOYENNE_SECTION: 'GRANDE_SECTION',
  GRANDE_SECTION: 'CP1',
  CP1: 'CP2',
  CP2: 'CE1',
  CE1: 'CE2',
  CE2: 'CM1',
  CM1: 'CM2',
  CM2: 'SIXIEME',
  SIXIEME: 'CINQUIEME',
  CINQUIEME: 'QUATRIEME',
  QUATRIEME: 'TROISIEME',
  TROISIEME: 'SECONDE',
  SECONDE: 'PREMIERE',
  PREMIERE: 'TERMINALE',
  ANNEE_7: 'ANNEE_8',
  ANNEE_8: 'ANNEE_9',
  ANNEE_9: 'ANNEE_10',
  ANNEE_10: null,  // Nécessite choix de spécialisation
  ANNEE_11_SCIENCES_EXP: 'ANNEE_12_SCIENCES_EXP',
  ANNEE_11_SCIENCES_MATH: 'ANNEE_12_SCIENCES_MATH',
  ANNEE_11_SCIENCES_SOC: 'ANNEE_12_SCIENCES_SOC',
  ANNEE_12_SCIENCES_EXP: 'ANNEE_13_SCIENCES_EXP',
  ANNEE_12_SCIENCES_MATH: 'ANNEE_13_SCIENCES_MATH',
  ANNEE_12_SCIENCES_SOC: 'ANNEE_13_SCIENCES_SOC'
};

const StudentPromotionManagement = ({ user }) => {
  const [academicYear, setAcademicYear] = useState('2024-2025');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [decisions, setDecisions] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    decided: 0,
    pending: 0
  });

  useEffect(() => {
    loadStudents();
  }, [academicYear]);

  const loadStudents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/promotions/prepare?academic_year=${academicYear}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setStudents(response.data.students || []);
      updateStats(response.data.students || []);
    } catch (error) {
      console.error('Erreur lors du chargement des étudiants:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const updateStats = (studentsList) => {
    const total = studentsList.length;
    const decided = Object.keys(decisions).length;
    setStats({
      total,
      decided,
      pending: total - decided
    });
  };

  const handleDecisionChange = (studentId, field, value) => {
    setDecisions(prev => {
      const newDecisions = { ...prev };
      if (!newDecisions[studentId]) {
        const student = students.find(s => s.student_id === studentId);
        newDecisions[studentId] = {
          student_id: studentId,
          current_level: student.current_level,
          decision: 'ADMIS',
          next_level: NEXT_LEVEL_MAPPING[student.current_level] || null,
          notes: ''
        };
      }
      
      newDecisions[studentId] = {
        ...newDecisions[studentId],
        [field]: value
      };

      // Si le niveau actuel est défini, mettre à jour le niveau suivant automatiquement
      if (field === 'current_level') {
        const nextLevel = NEXT_LEVEL_MAPPING[value];
        if (nextLevel !== undefined) {
          newDecisions[studentId].next_level = nextLevel;
        } else {
          newDecisions[studentId].next_level = null;
        }
      }

      // Si la décision est ADMIS ou PASSAGE_ANTICIPE, définir automatiquement next_level
      if (field === 'decision' && (value === 'ADMIS' || value === 'PASSAGE_ANTICIPE')) {
        const currentLevel = newDecisions[studentId].current_level;
        const nextLevel = NEXT_LEVEL_MAPPING[currentLevel];
        // Si le niveau suivant est null (ex: ANNEE_10 nécessite spécialisation), laisser l'utilisateur choisir
        if (nextLevel !== undefined) {
          newDecisions[studentId].next_level = nextLevel;
        } else {
          newDecisions[studentId].next_level = null;
        }
      }
      
      // Si la décision est REDOUBLANT, QUITTE ou TRANSFERE, next_level = null
      if (field === 'decision' && ['REDOUBLANT', 'QUITTE', 'TRANSFERE'].includes(value)) {
        newDecisions[studentId].next_level = null;
      }

      return newDecisions;
    });
  };

  const executePromotions = async () => {
    if (Object.keys(decisions).length === 0) {
      toast.error('Aucune décision n\'a été prise');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        academic_year: academicYear,
        decisions: Object.values(decisions)
      };
      
      // Log pour debug
      console.log('📤 Envoi des décisions:', JSON.stringify(payload, null, 2));
      
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/promotions/execute`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const { processed, errors, summary } = response.data;

      if (errors && errors.length > 0) {
        toast.warning(`${processed} décisions traitées, ${errors.length} erreurs`);
      } else {
        toast.success(`${processed} décisions traitées avec succès !`);
      }

      // Afficher le résumé
      const summaryText = Object.entries(summary)
        .filter(([_, count]) => count > 0)
        .map(([status, count]) => `${PROMOTION_STATUS[status].label}: ${count}`)
        .join(', ');
      
      if (summaryText) {
        toast.info(`Résumé: ${summaryText}`);
      }

      // Réinitialiser et recharger
      setDecisions({});
      setShowConfirmDialog(false);
      await loadStudents();
      
    } catch (error) {
      console.error('Erreur lors de l\'exécution des promotions:', error);
      toast.error('Erreur lors de l\'exécution des promotions');
    } finally {
      setProcessing(false);
    }
  };

  const getDecisionForStudent = (studentId) => {
    return decisions[studentId] || null;
  };

  useEffect(() => {
    updateStats(students);
  }, [decisions, students]);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* En-tête */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Passage au Niveau Supérieur
        </h1>
        <p className="text-gray-600">
          Gérez les décisions de promotion des étudiants pour l'année scolaire {academicYear}
        </p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total étudiants</p>
                <p className="text-3xl font-bold">{stats.total}</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Décisions prises</p>
                <p className="text-3xl font-bold text-green-600">{stats.decided}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">En attente</p>
                <p className="text-3xl font-bold text-orange-600">{stats.pending}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sélection de l'année scolaire */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Année scolaire</CardTitle>
          <CardDescription>
            Sélectionnez l'année scolaire pour la gestion des promotions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={academicYear} onValueChange={setAcademicYear}>
            <SelectTrigger className="w-full md:w-64">
              <SelectValue placeholder="Sélectionner l'année" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2023-2024">2023-2024</SelectItem>
              <SelectItem value="2024-2025">2024-2025</SelectItem>
              <SelectItem value="2025-2026">2025-2026</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Liste des étudiants */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Liste des étudiants</CardTitle>
              <CardDescription>
                Prenez des décisions pour chaque étudiant
              </CardDescription>
            </div>
            <Button 
              onClick={() => setShowConfirmDialog(true)}
              disabled={Object.keys(decisions).length === 0 || processing}
            >
              {processing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Traitement...
                </>
              ) : (
                <>
                  Valider les décisions ({Object.keys(decisions).length})
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : students.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucun étudiant trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Étudiant</TableHead>
                    <TableHead>Niveau actuel</TableHead>
                    <TableHead>Classe</TableHead>
                    <TableHead>Moyenne</TableHead>
                    <TableHead>Absences</TableHead>
                    <TableHead>Décision</TableHead>
                    <TableHead>Niveau suivant</TableHead>
                    <TableHead>Remarques</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map((student) => {
                    const decision = getDecisionForStudent(student.student_id);
                    return (
                      <TableRow key={student.student_id}>
                        <TableCell className="font-medium">
                          {student.first_name} {student.last_name}
                        </TableCell>
                        <TableCell>
                          {student.current_level ? (
                            CLASS_LEVELS[student.current_level] || student.current_level
                          ) : (
                            <Select
                              value={decision?.current_level || ''}
                              onValueChange={(value) => 
                                handleDecisionChange(student.student_id, 'current_level', value)
                              }
                            >
                              <SelectTrigger className="w-40">
                                <SelectValue placeholder="⚠️ Définir niveau..." />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(CLASS_LEVELS).map(([key, label]) => (
                                  <SelectItem key={key} value={key}>
                                    {label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell>
                          {student.current_class ? student.current_class.name : '-'}
                        </TableCell>
                        <TableCell>
                          {student.academic_stats.average_grade !== null ? (
                            <span className={`font-semibold ${
                              student.academic_stats.average_grade >= 10 
                                ? 'text-green-600' 
                                : 'text-red-600'
                            }`}>
                              {student.academic_stats.average_grade}/20
                            </span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={`${
                            student.academic_stats.absences > 10 
                              ? 'text-red-600 font-semibold' 
                              : ''
                          }`}>
                            {student.academic_stats.absences}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={decision?.decision || ''}
                            onValueChange={(value) => 
                              handleDecisionChange(student.student_id, 'decision', value)
                            }
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue placeholder="Choisir..." />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(PROMOTION_STATUS).map(([key, { label }]) => (
                                <SelectItem key={key} value={key}>
                                  {label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {decision && (decision.decision === 'ADMIS' || decision.decision === 'PASSAGE_ANTICIPE') ? (
                            <Select
                              value={decision.next_level || ''}
                              onValueChange={(value) => 
                                handleDecisionChange(student.student_id, 'next_level', value)
                              }
                            >
                              <SelectTrigger className="w-40">
                                <SelectValue placeholder="Niveau..." />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(CLASS_LEVELS).map(([key, label]) => (
                                  <SelectItem key={key} value={key}>
                                    {label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {decision && (
                            <input
                              type="text"
                              placeholder="Remarques..."
                              value={decision.notes || ''}
                              onChange={(e) => 
                                handleDecisionChange(student.student_id, 'notes', e.target.value)
                              }
                              className="border rounded px-2 py-1 text-sm w-40"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de confirmation */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer les décisions de promotion</DialogTitle>
            <DialogDescription>
              Vous êtes sur le point de valider {Object.keys(decisions).length} décision(s) 
              pour l'année scolaire {academicYear}.
              <br /><br />
              <strong>Cette action est irréversible.</strong> Les niveaux et classes des étudiants 
              seront modifiés selon les décisions prises.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <h4 className="font-semibold mb-2">Résumé des décisions :</h4>
            <div className="space-y-2">
              {Object.entries(PROMOTION_STATUS).map(([key, { label, icon: Icon, color }]) => {
                const count = Object.values(decisions).filter(d => d.decision === key).length;
                if (count === 0) return null;
                return (
                  <div key={key} className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span className="font-medium">{label}:</span>
                    <span>{count} étudiant(s)</span>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={processing}
            >
              Annuler
            </Button>
            <Button
              onClick={executePromotions}
              disabled={processing}
            >
              {processing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Traitement...
                </>
              ) : (
                'Confirmer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentPromotionManagement;
